import { paperDimsMm } from "./paper.js";
import { sheetMarksFor } from "./sheet-mark-geometry.js";
import type { PageDef, PageSheetMarks, PaperOrientation, PaperSize } from "./types.js";

/**
 * Paginators turn a board (Tactics frames, Whiteboard content) into a list
 * of {@link PageDef}s — the lego pieces every renderer/exporter consumes.
 *
 * Three strategies cover the requested matrix:
 *
 *   1. {@link framesAsPages}  — one page per tactics frame
 *   2. {@link viewportTiles}  — auto-tile a board rectangle into N pages
 *                                that respect the paper aspect ratio
 *   3. {@link manualPages}    — caller already has explicit page-frames
 */

let pageCounter = 0;
function nextId(prefix: string): string {
  pageCounter += 1;
  return `${prefix}-${pageCounter}-${Date.now().toString(36)}`;
}

/* ─── frames-as-pages (tactics) ───────────────────────────────────── */

export interface FramesAsPagesArgs {
  frames: ReadonlyArray<{ id: string; title?: string }>;
  /** Board-space rectangle that holds the whole pitch (typically 0,0,boardW,boardH). */
  board: { x: number; y: number; w: number; h: number };
  paper: PaperSize;
  orientation: PaperOrientation;
  background?: string;
}

/** One page per frame, all sharing the same board rectangle + paper format. */
export function framesAsPages(args: FramesAsPagesArgs): PageDef[] {
  return args.frames.map((f, i) => ({
    id: nextId(`frame-${f.id}`),
    title: f.title ?? `Frame ${i + 1}`,
    paper: args.paper,
    orientation: args.orientation,
    board: args.board,
    background: args.background,
    payload: { frameIndex: i, frameId: f.id },
  }));
}

/* ─── tile-at-scale (current view / single frame, with scaling) ───── */

export interface TileBoardAtScaleArgs {
  /** Full board rectangle that should be rendered. */
  board: { x: number; y: number; w: number; h: number };
  paper: PaperSize;
  orientation: PaperOrientation;
  /**
   * Multiplier **relative to fit-to-one-page**:
   *   - 1.0 → whole board fits on a single page (with aspect-preserving
   *     letterbox if board aspect ≠ paper aspect)
   *   - 2.0 → board is printed twice as large; needs a 2×2 grid of pages
   *     (assuming board & paper aspects match)
   *   - <1 → still produces one page; the board appears smaller
   */
  scale: number;
  /**
   * Physical overlap in **millimetres** shared between neighbouring tiles at
   * their seams, so the printed sheets can be glued/taped together cleanly.
   * The content scale stays constant across all tiles — an overlap simply
   * makes adjacent tiles duplicate a strip of the board at the join. Defaults
   * to 0 (tiles abut exactly). Only relevant when the grid is >1 tile.
   */
  overlapMm?: number;
  /**
   * Emit cut lines and glue areas on the tiles. Only meaningful together with
   * `overlapMm` and a grid larger than one page — see `sheet-marks.ts`.
   */
  sheetMarks?: boolean;
  /**
   * Millimetres trimmed off the upper sheet at a seam. Must be at least the
   * printer's unprintable margin, and small enough to leave a glue area:
   * `trimMm + printerMargin + glueArea <= overlapMm`. Defaults to half the
   * overlap, which only holds for a borderless printer.
   */
  trimMm?: number;
  background?: string;
  /** Stable prefix for ids. */
  idPrefix?: string;
  /**
   * Base title for the tile(s). Single-tile exports use this as-is;
   * multi-tile exports append `" r·c"`.
   */
  title?: string;
  /**
   * Optional payload copied onto every emitted page (e.g. tactics frame
   * index). Lets the surface renderer pick the right slice.
   */
  payload?: unknown;
}

/**
 * Pure layout calculator for {@link tileBoardAtScale}. Exposes the grid
 * (cols × rows) and per-page board-units so the dialog can render a
 * preview / page count without building the full PageDef list.
 */
export function tileGridForBoard(args: TileBoardAtScaleArgs): {
  cols: number;
  rows: number;
  pageBoardW: number;
  pageBoardH: number;
  /** Board-units advanced between adjacent tile origins (= page − overlap). */
  stepBoardW: number;
  stepBoardH: number;
  /** Overlap between adjacent tiles, in board units (0 when overlapMm = 0). */
  overlapBoardW: number;
  overlapBoardH: number;
} {
  const { wMm, hMm } = paperDimsMm(args.paper, args.orientation);
  const s = Math.max(0.05, args.scale);
  // Board units per mm of paper at scale 1 = "fit-to-page" larger-axis ratio.
  const boardPerMmAtFit = Math.max(args.board.w / wMm, args.board.h / hMm);
  const boardPerMm = boardPerMmAtFit / s;
  const pageBoardW = wMm * boardPerMm;
  const pageBoardH = hMm * boardPerMm;
  // Overlap in board units. Clamp below the page size so a tile always
  // advances (step > 0) even if a silly overlap is requested.
  const overlapMm = Math.max(0, args.overlapMm ?? 0);
  const overlapBoardW = Math.min(overlapMm * boardPerMm, pageBoardW * 0.8);
  const overlapBoardH = Math.min(overlapMm * boardPerMm, pageBoardH * 0.8);
  const stepBoardW = pageBoardW - overlapBoardW;
  const stepBoardH = pageBoardH - overlapBoardH;
  // First tile covers `pageBoard`; each extra tile advances by `step`. Cover
  // the whole board: extra = ceil((board − page) / step). Reduces to the
  // no-overlap `ceil(board / page)` when overlap = 0.
  const cols =
    args.board.w <= pageBoardW + 1e-6
      ? 1
      : 1 + Math.ceil((args.board.w - pageBoardW) / stepBoardW - 1e-6);
  const rows =
    args.board.h <= pageBoardH + 1e-6
      ? 1
      : 1 + Math.ceil((args.board.h - pageBoardH) / stepBoardH - 1e-6);
  return {
    cols,
    rows,
    pageBoardW,
    pageBoardH,
    stepBoardW,
    stepBoardH,
    overlapBoardW,
    overlapBoardH,
  };
}

/**
 * Tile a board rectangle across one or more paper pages at a user-chosen
 * scale, **preserving the board's aspect ratio**. The board is always
 * centered within the cols×rows grid — so when the grid is slightly
 * larger than the board (e.g. aspect mismatch) the empty margin is split
 * evenly on all sides instead of being dumped on the right + bottom.
 * At `scale = 1` the entire board appears letterboxed on one page; larger
 * scales tile across a grid so users can physically join the printed sheets.
 */
export function tileBoardAtScale(args: TileBoardAtScaleArgs): PageDef[] {
  const { cols, rows, pageBoardW, pageBoardH, stepBoardW, stepBoardH } = tileGridForBoard(args);
  // Total board span the grid covers. With overlap the tiles advance by
  // `step` (< page), so the span is page + (n−1)·step, not n·page.
  const gridW = pageBoardW + (cols - 1) * stepBoardW;
  const gridH = pageBoardH + (rows - 1) * stepBoardH;
  // Center the board inside the grid — the first tile's top-left starts
  // BEFORE the board origin when the grid is larger than the board.
  const startX = args.board.x + (args.board.w - gridW) / 2;
  const startY = args.board.y + (args.board.h - gridH) / 2;
  const pages: PageDef[] = [];
  const prefix = args.idPrefix ?? "tile";
  const baseTitle = args.title;
  const overlapMm = args.overlapMm ?? 0;
  const wantsMarks = Boolean(args.sheetMarks) && overlapMm > 0 && cols * rows > 1;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      // Each seam is trimmed on exactly one of its two sheets and glued on the
      // other: sheets are laid left-to-right, top-to-bottom, so a sheet is cut
      // where a neighbour precedes it and glued where one follows.
      // See `sheet-marks.ts` for the full rationale.
      const marks: PageSheetMarks | undefined = wantsMarks
        ? sheetMarksFor({
            col: c,
            row: r,
            cols,
            rows,
            overlapMm,
            trimMm: args.trimMm ?? overlapMm / 2,
          })
        : undefined;
      pages.push({
        id: nextId(prefix),
        title: cols * rows === 1 ? baseTitle : `${baseTitle ?? "Seite"} ${r + 1}·${c + 1}`,
        paper: args.paper,
        orientation: args.orientation,
        board: {
          x: startX + c * stepBoardW,
          y: startY + r * stepBoardH,
          w: pageBoardW,
          h: pageBoardH,
        },
        background: args.background,
        payload: args.payload,
        ...(marks ? { sheetMarks: marks } : {}),
      });
    }
  }
  return pages;
}

/* ─── poster tiling (large format printed onto small sheets) ─────── */

/** Position of a page within a poster grid, used to place cut marks. */
export interface PosterTilePosition {
  col: number;
  row: number;
  cols: number;
  rows: number;
  /** Overlap shared with each neighbour, in millimetres. */
  overlapMm: number;
}

export interface PosterTilingArgs {
  board: { x: number; y: number; w: number; h: number };
  /** The poster's nominal size — what it measures once taped together. */
  posterPaper: PaperSize;
  posterOrientation: PaperOrientation;
  /** The sheet actually fed into the printer (A4 in practice). */
  printPaper: PaperSize;
  /** Glue margin shared between neighbouring sheets. */
  overlapMm: number;
}

export interface PosterTiling {
  cols: number;
  rows: number;
  /** Orientation the print sheets must be fed in. */
  printOrientation: PaperOrientation;
  /** Scale to hand {@link tileBoardAtScale} so the grid comes out cols×rows. */
  scale: number;
  /** Printed size of the finished poster in mm, overlaps already deducted. */
  posterWMm: number;
  posterHMm: number;
}

/** DIN A series index — "A4" → 4. Null for Letter/Legal/16:9 and friends. */
function dinIndex(paper: PaperSize): number | null {
  const m = /^A([0-8])$/.exec(paper.id);
  return m ? Number(m[1]) : null;
}

function flip(o: PaperOrientation): PaperOrientation {
  return o === "landscape" ? "portrait" : "landscape";
}

/**
 * Work out how to print a large poster across small sheets.
 *
 * Halving a DIN format rotates it — A3 is two A4 *portrait* sheets side by
 * side, A2 is a 2×2 grid of A4 *landscape*, A1 is 4×2 portrait. So the grid
 * and the sheet orientation both follow from the number of DIN steps between
 * poster and print sheet, exactly and without measuring:
 *
 *   steps = dinIndex(print) − dinIndex(poster)
 *   cols  = 2^ceil(steps/2)     rows = 2^floor(steps/2)
 *   sheets are rotated when `steps` is odd
 *
 * The scale is then chosen so the board fills exactly that grid **with the
 * overlap already deducted**. That keeps the sheet count at its natural
 * minimum: a 5 mm glue margin would otherwise push A2 from 4 sheets to 9,
 * because each seam eats into the covered area and tips the grid over a whole
 * extra row and column. The poster comes out ~1 % under its nominal size
 * instead — 589×415 mm rather than 594×420 for A2.
 *
 * Returns null when either format is not DIN A, or when the poster is not
 * actually larger than the sheet (nothing to tile).
 */
export function posterTiling(args: PosterTilingArgs): PosterTiling | null {
  const posterIdx = dinIndex(args.posterPaper);
  const printIdx = dinIndex(args.printPaper);
  if (posterIdx === null || printIdx === null) return null;
  const steps = printIdx - posterIdx;
  if (steps <= 0) return null;

  // The bigger factor belongs on the poster's LONG axis: for a landscape
  // poster that is the column count, for a portrait one the row count.
  // Getting this backwards collapses an odd-step portrait poster onto a
  // single sheet — A3 portrait is 1×2 A4 landscape, never 2×1.
  const major = 2 ** Math.ceil(steps / 2);
  const minor = 2 ** Math.floor(steps / 2);
  const portrait = args.posterOrientation === "portrait";
  const cols = portrait ? minor : major;
  const rows = portrait ? major : minor;
  const printOrientation = steps % 2 === 0 ? args.posterOrientation : flip(args.posterOrientation);

  const { wMm, hMm } = paperDimsMm(args.printPaper, printOrientation);
  const overlapMm = Math.max(0, args.overlapMm);
  // Millimetres of paper actually covered once every seam has given up its
  // overlap. Guard against a nonsensical overlap swallowing a whole sheet.
  const usableWMm = Math.max(1, cols * wMm - (cols - 1) * overlapMm);
  const usableHMm = Math.max(1, rows * hMm - (rows - 1) * overlapMm);

  const boardPerMmAtFit = Math.max(args.board.w / wMm, args.board.h / hMm);
  // The tighter of the two axes decides; a hair of slack keeps the tile
  // arithmetic in `tileGridForBoard` from rounding up into an extra sheet.
  const boardPerMm = Math.max(args.board.w / usableWMm, args.board.h / usableHMm) * 1.0005;

  return {
    cols,
    rows,
    printOrientation,
    scale: boardPerMmAtFit / boardPerMm,
    posterWMm: usableWMm,
    posterHMm: usableHMm,
  };
}

/* ─── current frame (tactics, single frame with scaling) ──────────── */

export interface CurrentFrameAsPagesArgs {
  frameIndex: number;
  frameId: string;
  frameTitle?: string;
  board: { x: number; y: number; w: number; h: number };
  paper: PaperSize;
  orientation: PaperOrientation;
  /** See {@link TileBoardAtScaleArgs.scale}. */
  scale: number;
  /** See {@link TileBoardAtScaleArgs.overlapMm}. */
  overlapMm?: number;
  /** See {@link TileBoardAtScaleArgs.sheetMarks}. */
  sheetMarks?: boolean;
  /** See {@link TileBoardAtScaleArgs.trimMm}. */
  trimMm?: number;
  background?: string;
}

/**
 * Tactics "aktuelles Feld" export: renders only the currently selected
 * frame at the chosen scale, tiling across pages if necessary.
 */
export function currentFrameAsPages(args: CurrentFrameAsPagesArgs): PageDef[] {
  return tileBoardAtScale({
    board: args.board,
    paper: args.paper,
    orientation: args.orientation,
    scale: args.scale,
    overlapMm: args.overlapMm,
    sheetMarks: args.sheetMarks,
    trimMm: args.trimMm,
    background: args.background,
    idPrefix: `frame-${args.frameId}`,
    title: args.frameTitle ?? `Frame ${args.frameIndex + 1}`,
    payload: { frameIndex: args.frameIndex, frameId: args.frameId },
  });
}

/* ─── viewport tiling (legacy, kept for back-compat) ──────────────── */

export interface ViewportTilesArgs {
  /** Full board rectangle that should be tiled. */
  board: { x: number; y: number; w: number; h: number };
  paper: PaperSize;
  orientation: PaperOrientation;
  /** Optional padding (board units) shared by all tiles, default 0. */
  paddingBoard?: number;
  background?: string;
  /** Stable prefix for ids. */
  idPrefix?: string;
}

/**
 * Tile a board rectangle into N pages that **all share the same aspect
 * ratio as the chosen paper format**. Each tile covers a piece of board
 * such that placing the tiles side-by-side reconstructs the original board.
 *
 * The number of columns/rows is derived from the board / paper aspect
 * ratio so paper is always fully used and no tile is letter-boxed.
 */
export function viewportTiles(args: ViewportTilesArgs): PageDef[] {
  const { wMm, hMm } = paperDimsMm(args.paper, args.orientation);
  const paperAspect = wMm / hMm;
  const boardAspect = args.board.w / args.board.h;

  // Pick the smallest grid that doesn't distort: how many pages fit along
  // each board axis if each page covers a board-rect of paperAspect?
  let cols = 1;
  let rows = 1;
  if (boardAspect > paperAspect) {
    cols = Math.max(1, Math.round(boardAspect / paperAspect));
    rows = 1;
  } else if (paperAspect > boardAspect) {
    rows = Math.max(1, Math.round(paperAspect / boardAspect));
    cols = 1;
  }
  // Tile cell size in board coordinates such that cell aspect == paper.
  const tileH = args.board.h / rows;
  const tileW = tileH * paperAspect;
  // If width doesn't add up, redistribute by clamping columns.
  const actualCols = Math.max(1, Math.ceil(args.board.w / tileW));

  const pages: PageDef[] = [];
  const prefix = args.idPrefix ?? "tile";
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < actualCols; c += 1) {
      pages.push({
        id: nextId(prefix),
        title: `Tile ${r + 1}·${c + 1}`,
        paper: args.paper,
        orientation: args.orientation,
        board: {
          x: args.board.x + c * tileW,
          y: args.board.y + r * tileH,
          w: tileW,
          h: tileH,
        },
        background: args.background,
      });
    }
  }
  return pages;
}

/* ─── manual page-frames ──────────────────────────────────────────── */

export interface ManualPageInput {
  id?: string;
  title?: string;
  /** Board rectangle to render. */
  board: { x: number; y: number; w: number; h: number };
  paper?: PaperSize;
  orientation?: PaperOrientation;
  background?: string;
}

export interface ManualPagesArgs {
  pages: readonly ManualPageInput[];
  /** Defaults applied to pages that don't override them. */
  defaultPaper: PaperSize;
  defaultOrientation: PaperOrientation;
  background?: string;
}

/**
 * Passthrough paginator for explicit, user-drawn page frames. Each input
 * becomes one `PageDef` with sensible defaults filled in.
 */
export function manualPages(args: ManualPagesArgs): PageDef[] {
  return args.pages.map((p, i) => ({
    id: p.id ?? nextId(`page-${i + 1}`),
    title: p.title ?? `Seite ${i + 1}`,
    paper: p.paper ?? args.defaultPaper,
    orientation: p.orientation ?? args.defaultOrientation,
    board: p.board,
    background: p.background ?? args.background,
  }));
}
